import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Badge, Button, Card, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { formatDate, type OriginTask } from './data';

// Piezas comunes del detalle de un alta y de una baja.

const TASK_TONES = { por_hacer: 'amber', en_curso: 'blue', terminado: 'slate', cancelado: 'slate' } as const;

/** Tareas generadas al aprobar: el avance del alta o de la baja. */
export function OriginTasksCard({ tasks, empty }: { tasks: OriginTask[]; empty: string }) {
  const done = tasks.filter((x) => x.statusCategory === 'terminado' || x.statusCategory === 'cancelado').length;
  return (
    <Card title={tasks.length ? t('us.tareas_avance', { hechas: done, total: tasks.length }) : t('us.tareas')}>
      {tasks.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm" aria-label={t('us.tareas')}>
          {tasks.map((x) => (
            <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                <Link to={`/tareas/${x.id}`} className={`font-medium hover:underline ${x.statusCategory === 'terminado' ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                  {x.title}
                </Link>
                <span className="block text-xs text-slate-500">
                  {x.assignees.join(', ') || '—'}
                  {x.dueDate && ` · ${t('us.vence', { fecha: formatDate(x.dueDate) })}`}
                </span>
              </span>
              <Badge tone={TASK_TONES[x.statusCategory]}>{x.status}</Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Quién decidió y por qué, o en espera de RR. HH. */
export function DecisionSummary({ status, decidedBy, decidedAt, reason }: { status: string; decidedBy: string | null; decidedAt: string | null; reason: string | null }) {
  if (status === 'pendiente') return <Alert tone="info">{t('us.espera_rrhh')}</Alert>;
  if (status === 'cancelada') return <Alert tone="info">{t('us.cancelada_info')}</Alert>;
  return (
    <Alert tone={status === 'rechazada' ? 'error' : 'success'}>
      <p>{status === 'rechazada' ? t('us.rechazo', { quien: decidedBy ?? '—', fecha: decidedAt ? formatDate(decidedAt) : '—' }) : t('us.aprobo', { quien: decidedBy ?? '—', fecha: decidedAt ? formatDate(decidedAt) : '—' })}</p>
      {reason && <p className="mt-1 whitespace-pre-wrap">{reason}</p>}
    </Alert>
  );
}

/**
 * Aprobar o rechazar (RR. HH.). `warning` va antes de los botones (en un alta, el costo); con `confirm`, aprobar exige
 * marcar que se leyó.
 */
export function DecisionCard({ path, help, warning, confirm, onDone }: { path: string; help: string; warning?: ReactNode; confirm?: string; onDone: (approved: boolean) => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(approved: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api(`${path}/decision`, { json: { approved, reason: reason.trim() || null } });
      onDone(approved);
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('us.decidir')}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">{help}</p>
        {error && <Alert>{error}</Alert>}
        {warning}
        {confirm && (
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-marca" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span>{confirm}</span>
          </label>
        )}
        <Textarea id="us-motivo" label={t('us.motivo')} rows={3} maxLength={2_000} value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          <Button disabled={busy || (!!confirm && !confirmed)} onClick={() => void decide(true)}>
            {t('us.aprobar')}
          </Button>
          <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => void decide(false)}>
            {t('us.rechazar')}
          </Button>
        </div>
        {!reason.trim() && <p className="text-xs text-slate-500">{t('us.motivo_ayuda')}</p>}
      </div>
    </Card>
  );
}

/** Quien pidió la solicitud la cancela mientras está pendiente. */
export function CancelCard({ path }: { path: string }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      await api(`${path}/cancelar`, { method: 'POST' });
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }
  return (
    <Card>
      <div className="space-y-2 text-sm">
        {error && <Alert>{error}</Alert>}
        {confirming ? (
          <Alert tone="warning">
            <p>{t('us.cancelar_confirmar')}</p>
            <div className="mt-2 flex gap-2">
              <Button variant="danger" disabled={busy} onClick={() => void cancel()}>
                {t('us.si_cancelar')}
              </Button>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('comun.volver')}
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            <p className="text-slate-600">{t('us.cancelar_ayuda')}</p>
            <Button variant="secondary" onClick={() => setConfirming(true)}>
              {t('us.cancelar_solicitud')}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
